import {
  formatoFechaBolivia,
  formatoNumero,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import { pisoEnTexto, techoEnTexto } from '@/features/pricing-rules/components/barra-de-rangos';
import { permitidoEnTexto } from '@/features/pricing-rules/lib/limites-del-producto';
import type { VueltaRevisada } from '../types/historial-de-precios.types';
import type { PorQueNoSeDeshace } from '../types/price-proposals.types';
import { quienOQue } from './historial-de-precios';

/**
 * Lo que dice la ventana «¿Volver a X?» (lote C2b, spec 246 punto 11, D15;
 * maqueta `c2-volver`), en palabras. Puro: el componente sólo pinta. Las dos
 * variantes de la maqueta: el precio de entonces cumple las reglas de hoy (se
 * elige qué pasa con la estrategia) o no las cumple (se elige el más cercano
 * que cumple o volver igual). Si pasan las dos cosas, se ven las dos.
 */

const fmt = (n: number) => formatoPrecio(n);
const minuscula = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
const CENTAVO = 0.005;

/**
 * «el precio del 28 oct 2026 (a mano, en la ficha)»; al deshacer, «el que tenía
 * antes del cambio del 5 oct 2026 (aprobación de una propuesta)».
 */
export function deCuandoEsElPrecio(v: VueltaRevisada, alPrecioDeAntes = false): string {
  const via = quienOQue({
    id: v.cambio.id,
    cuando: v.cambio.cuando,
    antes: v.cambio.antes,
    despues: v.cambio.precio,
    via: v.cambio.via,
    quien: null,
    soporte: null,
    fuente: null,
    motivo: null,
    propuestaId: null,
    cambioId: v.cambio.id,
    volvioA: null,
    deshizoEl: null,
    sola: false,
    deAntes: false,
  }).titulo;
  // Al deshacer (ronda del 5-oct), el precio no es el de ese cambio: es el de antes.
  return alPrecioDeAntes
    ? `el que tenía antes del cambio del ${formatoFechaBolivia(v.cambio.cuando)} (${minuscula(via)})`
    : `el precio del ${formatoFechaBolivia(v.cambio.cuando)} (${minuscula(via)})`;
}

/**
 * Al deshacer, si la revisión dice que ya no se puede (`deshacible: false`): el
 * mismo motivo que da el servidor con su 409, sin el precio.
 */
export const NO_SE_DESHACE =
  'Después de este cambio hubo otro: ' +
  'no se puede deshacer sin pisarlo. Para elegir un precio, usá «Volver a …» en su historial (desde la bandeja: abrí la fila y entrá a «Ver todo su historial»).';

/**
 * Ronda 1 (I1): por qué no se deshace, en palabras — los mismos textos que los
 * 409/422 del servidor. Sin motivo (un backend viejo), el de «hubo otro».
 */
export function porQueNoSeDeshaceEnTexto(motivo: PorQueNoSeDeshace | null | undefined): string {
  switch (motivo) {
    case 'YA_DESHECHA':
      return 'Ese cambio ya se deshizo: no hay nada más que deshacer.';
    case 'SIN_CAMBIO':
      return 'Ese cambio no movió el precio: no hay nada que deshacer.';
    case 'SIN_PRECIO_ANTES':
      return 'Ese cambio no tiene precio de antes: el producto nació con él.';
    default:
      return NO_SE_DESHACE;
  }
}

/** «↑ sube 625,01 (+11,6 %)» / «↓ baja 1.074,99 (−20,0 %)». */
export function cuantoCambia(hoy: number, precio: number): { texto: string; sube: boolean } {
  const d = precio - hoy;
  const pct = hoy > 0 ? formatoPorcentaje((d / hoy) * 100, { signo: true }) : null;
  const cuanto = `${fmt(Math.abs(d))}${pct ? ` (${pct})` : ''}`;
  return d >= 0
    ? { texto: `↑ sube ${cuanto}`, sube: true }
    : { texto: `↓ baja ${cuanto}`, sube: false };
}

/**
 * «ganás 34,6 %». null sin costo: no se dice «ganás» (ausente es «no sé»).
 * Debajo del costo no se «gana» un número negativo (auditoría H6).
 */
export const margenEnTexto = (margen: number | null) =>
  margen == null
    ? null
    : margen < 0
      ? `queda debajo del costo (pierde ${formatoPorcentaje(-margen)})`
      : `ganás ${formatoPorcentaje(margen)}`;

export type TonoDeLasReglas = 'ok' | 'bloqueo';

/**
 * El aviso de las reglas. «Cumple tus 4 reglas · Queda permitido de 5.000,00 a
 * 9.144,00.» o «No cumple 2 reglas · «No ganar menos de 18 %» deja desde
 * 4.775,92 y «No vender por debajo de Bs 5.000,00», desde 5.000,00.»
 */
export function reglasDeLaVuelta(v: VueltaRevisada): {
  tono: TonoDeLasReglas;
  titulo: string;
  detalle: string | null;
} {
  const r = v.revision;
  if (r.cumple) {
    const reglas = new Set(r.rangos.filter((x) => x.aviso == null).map((x) => x.ruleId)).size;
    const permitido = permitidoEnTexto(r.cruce);
    return {
      tono: 'ok',
      titulo:
        reglas === 0
          ? 'Ninguna regla lo alcanza'
          : reglas === 1
            ? 'Cumple tu regla'
            : `Cumple tus ${reglas} reglas`,
      detalle: permitido ? `Queda permitido ${permitido}.` : null,
    };
  }
  const n = r.rompe.length;
  const cada = r.rompe.map((x, i) => {
    const tramo =
      x.min != null
        ? `desde ${pisoEnTexto(x.min)}`
        : x.max != null
          ? `hasta ${techoEnTexto(x.max)}`
          : '';
    return i === 0 ? `«${x.ruleName}» deja ${tramo}` : `«${x.ruleName}», ${tramo}`;
  });
  return {
    tono: 'bloqueo',
    titulo: n === 1 ? 'No cumple una regla' : `No cumple ${n} reglas`,
    detalle: r.sinPrecioPosible
      ? 'Conflicto entre reglas: ningún precio las cumple todas.'
      : `${cada.join(' y ')}.`,
  };
}

export type OpcionDePrecio = 'USAR' | 'IGUAL';

/** Sólo si no cumple: el más cercano que cumple (si hay) o volver igual. */
export function opcionesDePrecio(
  v: VueltaRevisada
): Array<{ clave: OpcionDePrecio; titulo: string; detalle: string; precio: number }> | null {
  const r = v.revision;
  if (r.cumple) return null;
  const precio = v.cambio.precio;
  const marcadas =
    r.rompe.length === 1
      ? 'La regla queda marcada en su ficha como no cumplida.'
      : 'Las reglas quedan marcadas en su ficha como no cumplidas.';
  return [
    ...(r.sugerido != null
      ? [
          {
            clave: 'USAR' as const,
            titulo: `Usar ${fmt(r.sugerido)}`,
            detalle: `El más cercano a ${fmt(precio)} que cumple tus reglas.`,
            precio: r.sugerido,
          },
        ]
      : []),
    { clave: 'IGUAL' as const, titulo: `Volver a ${fmt(precio)} igual`, detalle: marcadas, precio },
  ];
}

export type OpcionDeEstrategia = 'DEJAR' | 'SACAR';

/**
 * Auditoría H2: si la estrategia, cuando vuelva a pedir, lo pone sola o te
 * pregunta. La misma cuenta que `aplicaSolo` del servidor: «siempre te
 * pregunta» pregunta; «aplica sola» pregunta si queda debajo del costo o no
 * tiene costo (lote C1), o si el cambio supera su umbral. null = lo pone sola.
 */
function porQuePregunta(
  e: NonNullable<VueltaRevisada['estrategia']>,
  pide: number,
  precioFinal: number,
  costo: number | null
): string | null | 'SIEMPRE' {
  if (e.mode !== 'AUTO' && e.mode !== 'AUTO_WITH_RULES') return 'SIEMPRE';
  if (costo == null || costo <= 0) return 'no tiene costo';
  if (pide < costo - CENTAVO) return 'quedaría debajo del costo';
  if (e.askAboveChangePct != null) {
    const pct = precioFinal > 0 ? (Math.abs(pide - precioFinal) / precioFinal) * 100 : Infinity;
    if (pct > e.askAboveChangePct + 1e-9)
      return `el cambio supera ${formatoNumero(e.askAboveChangePct)} %`;
  }
  return null;
}

const LLEGA_A_POR_DECIDIR = 'Llega a Por decidir.';

/** «Sostener el margen objetivo» lo toma: con los datos de hoy pide 5.364,99. null sin estrategia. */
export function estrategiaDeLaVuelta(
  v: VueltaRevisada,
  precioFinal: number
): {
  titulo: string;
  opciones: Array<{ clave: OpcionDeEstrategia; titulo: string; detalle: string }>;
} | null {
  const e = v.estrategia;
  if (!e) return null;
  const cuando = e.esperaCompetencia ? 'Cuando cambie su competencia, ' : 'En su próxima corrida ';
  let queHace: string;
  if (e.pide == null)
    // Ronda 1 (M2): sin precio posible, la corrida igual deja una pendiente si su
    // precio no es el de hoy (`decision-de-la-corrida.ts`: nunca la aplica sola);
    // la revisión no dice si es ese caso o falta de datos: se dice condicional.
    queHace = `«${e.name}» no puede calcularle un precio hoy: no lo va a mover sola. Si igual te propone uno, ${minuscula(LLEGA_A_POR_DECIDIR)}`;
  else if (Math.abs(e.pide - precioFinal) < CENTAVO)
    queHace = `«${e.name}» pide lo mismo: no lo va a mover.`;
  else {
    const pregunta = porQuePregunta(e, e.pide, precioFinal, v.costo);
    // DO-4 (ronda del 5-oct): la que pregunta deja una propuesta en Por decidir;
    // la que aplica sola no pasa por ahí (decirlo sería falso, auditoría A10).
    queHace =
      pregunta === 'SIEMPRE'
        ? `${cuando}te va a proponer ${fmt(e.pide)} de nuevo (siempre te pregunta). ${LLEGA_A_POR_DECIDIR}`
        : pregunta == null
          ? `${cuando}lo va a volver a poner en ${fmt(e.pide)} sola.`
          : `${cuando}te va a preguntar antes de volver a ponerlo en ${fmt(e.pide)} (${pregunta}). ${LLEGA_A_POR_DECIDIR}`;
  }
  return {
    // Ronda 5-oct: «con los datos de hoy», porque al deshacer una aprobación el
    // número suele diferir del aprobado (costo, mercado o redondeo de hoy).
    titulo:
      e.pide == null
        ? `«${e.name}» lo toma`
        : `«${e.name}» lo toma: con los datos de hoy pide ${fmt(e.pide)}`,
    opciones: [
      { clave: 'DEJAR', titulo: 'Volver y dejarlo en la estrategia', detalle: queHace },
      {
        clave: 'SACAR',
        titulo: 'Volver y sacarlo de la estrategia',
        detalle: 'Ninguna corrida lo vuelve a tocar hasta que lo agregues.',
      },
    ],
  };
}

type LoQueQuedo = {
  precio: number;
  reglas: { revisadas: true; rompe: unknown[] } | { revisadas: false };
  sacadoDe?: Array<{ name: string }>;
};

/** El aviso después de volver: qué quedó. `usadoElSugerido`: no volvió a ese
 *  precio (no cumplía): quedó en el más cercano que cumple. */
export function quedoEnTexto(r: LoQueQuedo, usadoElSugerido = false): string {
  const precio = usadoElSugerido ? `Quedó en ${fmt(r.precio)}.` : `Volvió a ${fmt(r.precio)}.`;
  const demas = loDemasDelAviso(r);
  return demas ? `${precio} ${demas}` : precio;
}

/**
 * Lo que sigue al precio en el aviso (reglas, estrategia), para cuando el título
 * ya dice el precio (ronda del 5-oct: no repetirlo). null si no hay nada más.
 */
export function loDemasDelAviso(r: LoQueQuedo): string | null {
  const partes: string[] = [];
  if (r.reglas.revisadas && r.reglas.rompe.length > 0)
    partes.push(
      r.reglas.rompe.length === 1
        ? 'No cumple una regla: queda marcada en su ficha.'
        : `No cumple ${r.reglas.rompe.length} reglas: quedan marcadas en su ficha.`
    );
  if (!r.reglas.revisadas) partes.push('No se pudieron revisar tus reglas.');
  if (r.sacadoDe && r.sacadoDe.length > 0)
    partes.push(`Lo sacaste de ${r.sacadoDe.map((s) => `«${s.name}»`).join(', ')}.`);
  return partes.length > 0 ? partes.join(' ') : null;
}
